import { useContext } from 'react';
import * as yup from 'yup';
import { LanguageContext } from '../../../core/TenantProvider/contexts';
import TranslateWrapper from '../../../core/utils/TranslateWrapper';
import { COUNTRY_WISE_BANK_DETAILS_FIELDS } from '../../../core/utils/Constants/Constants';
import { isCountryPresent } from './fieldsMappingDefinition';

export const ValidationSchema = (bankDetailsValidation: boolean, BANK_DOCUMENT_UPLOAD: boolean) => {

    const { translations } = useContext(LanguageContext);
    const translate = TranslateWrapper(translations);

    const accountNameMaxError: string = translate('add.bank.details.form.accountname.label.max.error');
    const blackListedError: string = translate('add.bank.details.form.country.blacklisted.error');
    const accountNumberMaxError: string = translate('add.bank.details.form.account.number.label.max.error');
    const sortCodeValidationError: string = translate('add.bank.details.form.sort.code.validation.error');
    const bankNameMaxError: string = translate('bank.account.lookup.bank_name.label.max.error');
    const bankAdressMaxError: string = translate('bank.account.lookup.bank_address.label.max.error');

    return yup
        .object({
            accountName: yup
                .string()
                .trim()
                .max(40, accountNameMaxError)
                .required(''),

            currency: yup
                .string()
                .required(''),

            isCountryBlackListed: yup
                .boolean()
                .required(),

            country: yup.string()
                .when("isCountryBlackListed", {
                    is: true,
                    then: (schema) => schema.notRequired().test(
                        "invalid-country",
                        blackListedError,
                        () => false // Always return false to trigger validation error
                    ),
                    otherwise: (schema) => schema.required(''),
                }),

            accountNumber: yup.string().trim()
                .when(['accountName', 'currency', 'country'], {
                    is: (accountName, currency, country) => {
                        if (!accountName || !currency || !country) return false;
                        if (currency === 'GBP' && country === 'GBR') return true;
                        const shouldRenderDefault: boolean = isCountryPresent(country);
                        if (!shouldRenderDefault) return true;
                        const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'accountNumber');
                        return accountNumberField?.values.includes(country) ?? false;
                    },
                    then: (schema) => schema.max(20, accountNumberMaxError).required(''),
                    otherwise: (schema) => schema.notRequired()
                }),
            sortCode: yup.string().trim()
                .when(['accountName', 'currency', 'country'], {
                    is: (accountName, currency, country) => {
                        if (!accountName || !currency || !country) return false;
                        if (currency === 'GBP' && country === 'GBR') return true;

                        return false;
                    },
                    then: (schema) => schema
                        .required('')
                        .test(
                            'sortCode-format',
                            sortCodeValidationError,
                            (value) => {
                                if (!value) return false;
                                const digitsOnly = value.replace(/\D/g, '');
                                return /^\d{6}$/.test(digitsOnly);
                            }
                        ),
                    otherwise: (schema) => schema.notRequired()
                }),

            bsbNumber: yup.string().trim()
                .test('bsbNumber-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const bsbNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'bsbNumber');

                    if (bsbNumberField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (bsbNumberField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            swiftCode: yup.string().trim()
                .test('swiftCode-required', '', function (value) {
                    const { accountName, currency, country, bsbNumber, transitNumber, abaNumber, branchCode, ifscCodeField, iban, cnapsCodeNumberField } = this.parent;

                    if (!accountName || !currency || !country) return true;

                    if (currency === 'GBP' && country === 'GBR') return true;

                    const shouldRenderDefault: boolean = isCountryPresent(country);

                    const swiftCodeField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'swiftCode');
                    const isOptionalFieldsValid = bsbNumber !== '' || transitNumber !== '' || abaNumber !== '' || branchCode !== '' || ifscCodeField !== '' || iban !== '' || cnapsCodeNumberField !== '';

                    if (swiftCodeField?.values.includes(country) && value === '' && isOptionalFieldsValid) return true;

                    if (swiftCodeField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    if (!shouldRenderDefault && value === '') return this.createError({ message: '' });

                    return true;
                }),

            transitNumber: yup.string().trim()
                .test('transitNumber-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const transitNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'transitNumber');

                    if (transitNumberField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (transitNumberField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            abaNumber: yup.string().trim()
                .test('abaNumber-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const abaNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'abaNumber');

                    if (abaNumberField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (abaNumberField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            branchCode: yup.string().trim()
                .test('branchCode-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const branchCodeField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'branchCode');

                    if (branchCodeField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (branchCodeField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            ifscCode: yup.string().trim()
                .test('ifscCode-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const ifscCodeField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'ifscCode');

                    if (ifscCodeField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (ifscCodeField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            iban: yup.string().trim()
                .test('iban-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const ibanNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'iban');

                    if (ibanNumberField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (ibanNumberField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            cnapsCode: yup.string().trim()
                .test('cnapsCode-required', '', function (value) {
                    const { accountName, currency, country, swiftCode } = this.parent;

                    if (!accountName || !currency || !country) return true; // Skip validation

                    if (currency === 'GBP' && country === 'GBR') return true; // Skip for GBP/GBR

                    const cnapsCodeNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'cnapsCode');

                    if (cnapsCodeNumberField?.values.includes(country) && value === '' && swiftCode !== '') return true;

                    if (cnapsCodeNumberField?.values.includes(country) && value === '') return this.createError({ message: '' });

                    return true;
                }),

            affiliateBank: yup
                .string()
                .trim()
                .max(65, bankNameMaxError)
                .when([], {
                    is: () => {
                        return bankDetailsValidation
                    },
                    then: yup.string().required(''),
                    otherwise: yup.string().notRequired(),
                }),
            branchAddress: yup
                .string()
                .trim()
                .max(65, bankAdressMaxError)
                .when([], {
                    is: () => {
                        return bankDetailsValidation
                    },
                    then: yup.string().required(''),
                    otherwise: yup.string().notRequired(),
                }),
            file: yup.mixed()
                .when(['affiliateBank', 'branchAddress'], {
                    is: (affiliateBank, branchAddress) => {
                        if (!affiliateBank || !branchAddress) return false;
                        if (!BANK_DOCUMENT_UPLOAD) return false;

                        return true;
                    },
                    then: yup.mixed().required().default(null),
                    otherwise: yup.mixed().notRequired(),
                })
        })
        .required();
};